import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { supabase, registrarActividad } from '../supabaseClient';
import { useAuth } from "../context/AuthContext.jsx";
import { useRealtime } from '../hooks/useRealTime'; 
import NewsManager from '../componentes/NewsManager';

import '../styles/stylesNoticias.css';
import '../styles/NewsManager.css';

const SkeletonCard = () => (
    <div className="noticia-card skeleton">
        <div className="skeleton-img"></div>
        <div className="card-content">
            <div className="skeleton-line short"></div>
            <div className="skeleton-line long"></div>
            <div className="skeleton-line medium"></div>
        </div>
    </div>
);

const Noticias = () => {
    const { isAdmin, user } = useAuth();
    const [listaNoticias, setListaNoticias] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [loading, setLoading] = useState(false);
    const [feedback, setFeedback] = useState({ msg: "", type: "" });

    const [page, setPage] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const ITEMS_PER_PAGE = 6;

    const showFeedback = (msg, type) => {
        setFeedback({ msg, type });
        setTimeout(() => setFeedback({ msg: "", type: "" }), 4000);
    };

    // --- LÓGICA DE DATOS ---
    const fetchNoticias = useCallback(async (pageNum = 0, isInitial = false) => {
        try {
            setLoading(true);
            const from = pageNum * ITEMS_PER_PAGE;
            const to = from + ITEMS_PER_PAGE - 1;

            const { data, error } = await supabase
                .from('noticias')
                .select('*')
                .order('fecha', { ascending: false })
                .range(from, to);

            if (!error) {
                if (isInitial) {
                    setListaNoticias(data || []);
                } else {
                    setListaNoticias(prev => [...prev, ...(data || [])]);
                }
                if (data.length < ITEMS_PER_PAGE) setHasMore(false);
            }
        } finally {
            setLoading(false);
        }
    }, []);

    const handleRealtimeChanges = useCallback((payload) => {
        if (payload.eventType === 'INSERT') {
            setListaNoticias((prev) => {
                const exists = prev.some(n => n.id === payload.new.id);
                if (exists) return prev;
                return [payload.new, ...prev];
            });
        } else if (payload.eventType === 'DELETE') {
            setListaNoticias((prev) => prev.filter(n => n.id !== payload.old.id));
        } else if (payload.eventType === 'UPDATE') {
            setListaNoticias((prev) => prev.map(n => n.id === payload.new.id ? payload.new : n));
        }
    }, []);

    useRealtime('noticias', handleRealtimeChanges);

    useEffect(() => {
        fetchNoticias(0, true);
    }, [fetchNoticias]);

    const handleLoadMore = () => {
        const nextPage = page + 1;
        setPage(nextPage);
        fetchNoticias(nextPage);
    };

    // Reemplazá tu handleDelete actual por este:
    const handleDelete = useCallback(async (n) => {
        if (!window.confirm(`¿Estás seguro de eliminar: "${n.titulo}"?`)) return;

        try {
            setLoading(true);
            if (n.storage_path) {
                await supabase.storage.from('archivos').remove([n.storage_path]);
            }
            const { error: dbError } = await supabase.from('noticias').delete().eq('id', n.id);
            if (dbError) throw dbError;

            // Asegurate de que 'user' esté disponible (viene del hook useAuth al principio del componente)
            await registrarActividad(user.email, `Eliminó noticia: ${n.titulo}`);
            showFeedback("🗑️ Noticia eliminada", "success");
        } catch (err) {
            showFeedback(`❌ Error: ${err.message}`, "error");
        } finally {
            setLoading(false);
        }
    }, [user]); // <--- Agregamos esta dependencia al final

    const grillaMemoizada = useMemo(() => {
        return listaNoticias.map((noticia) => (
            <div key={noticia.id} className="noticia-card">
                <div className="card-img-container">
                    <img 
                        src={noticia.imagen_url || '/resources/comuna.jpg'} 
                        alt={noticia.titulo} 
                        loading="lazy" 
                        onError={(e) => {e.target.src = '/resources/comuna.jpg'}}
                    />
                </div>
                <div className="card-content">
                    <span className="card-date">
                        {new Date(noticia.fecha).toLocaleDateString('es-AR', { day: 'numeric', month: 'long', year: 'numeric' })}
                    </span>
                    <h3 className="card-title">{noticia.titulo}</h3>
                    <p className="card-excerpt">{noticia.resumen}</p>
                </div>
                <div className="card-footer-actions">
                    <Link to={`/noticias/${noticia.id}`} className="card-btn">Leer más</Link>
                    {isAdmin && (
                        <button
                            className="admin-btn-delete"
                            disabled={loading}
                            onClick={() => handleDelete(noticia)}
                            title="Eliminar noticia"
                        >
                            {loading ? "..." : "🗑️"}
                        </button>
                    )}
                </div>
            </div>
        ));
    }, [listaNoticias, isAdmin, loading, handleDelete]);

    return (
        <div className="noticias-page">
            {feedback.msg && (
                <div className={`notification-toast ${feedback.type}`}>
                    {feedback.msg}
                </div>
            )}

            <header className="noticias-header-centrado">
                <h1>Noticias de Santo Domingo</h1>
                <p>Novedades institucionales, obras y eventos de la comuna</p>
            </header>

            <div className="noticias-container">
                <div className="noticias-grid">
                    {/* Tarjeta de Crear (Solo Admin) */}
                    {isAdmin && (
                        <div className="noticia-card admin-plus-card" onClick={() => setShowModal(true)}>
                            <div className="admin-plus-inner">
                                <div className="admin-plus-circle">
                                    <span className="admin-plus-symbol">+</span>
                                </div>
                                <span className="admin-plus-label">Nueva Noticia</span>
                            </div>
                        </div>
                    )}
                    
                    {/* Skeleton Loading */}
                    {loading && listaNoticias.length === 0 ? (
                        [...Array(6)].map((_, i) => <SkeletonCard key={i} />)
                    ) : (
                        grillaMemoizada
                    )}
                </div>

                {hasMore && (
                    <div className="load-more-container">
                        <button
                            onClick={handleLoadMore}
                            className="btn-load-more"
                            disabled={loading}
                        >
                            {loading ? "Cargando..." : "Ver noticias anteriores"}
                        </button>
                    </div>
                )}
            </div>

            {/* Modal */}
            {showModal && (
                <div className="modal-overlay" onClick={() => setShowModal(false)}>
                    <div className="modal-content" onClick={e => e.stopPropagation()}>
                        <NewsManager
                            onClose={() => setShowModal(false)}
                            onFeedback={showFeedback}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

export default Noticias;